import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import SEOHead from '@/components/base/SEOHead';
import ScrollToTop from '@/components/base/ScrollToTop';
import { aboutData } from '@/mocks/about';
import { useState } from 'react';

const aboutSchema = [
  {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于我们 - 水野朝阳官方粉丝站',
    description: '了解水野朝阳官方粉丝站的品牌故事、发展历程与联系方式。我们致力于为全球粉丝提供最权威、最全面的水野朝阳信息聚合服务。',
    url: 'https://zhangzaichao.cn/about',
    isPartOf: {
      '@type': 'WebSite',
      name: '水野朝阳官方粉丝站',
      url: 'https://zhangzaichao.cn',
    },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '水野朝阳官方粉丝站',
    url: 'https://zhangzaichao.cn',
    description: '水野朝阳官方粉丝站是由资深粉丝团队创建与维护的非营利性信息聚合平台。',
    foundingDate: '2024-03',
    email: 'contact@zhangzaichao.cn',
    sameAs: [
      'https://twitter.com/mizuno_asahi',
      'https://instagram.com/mizuno_asahi',
    ],
  },
];

const CONTACT_FORM_URL = 'https://readdy.ai/api/form/d9u4q7d3pcjqs2fcsm7g';

export default function AboutPage() {
  const [formState, setFormState] = useState<'idle' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('phone_alt') as string;
    if (honeypot && honeypot.trim() !== '') {
      setFormState('success');
      setFormMessage('您的消息已成功发送，我们会尽快回复！');
      form.reset();
      return;
    }

    try {
      const params = new URLSearchParams();
      formData.forEach((value, key) => {
        if (key !== 'phone_alt' && value) {
          params.append(key, value as string);
        }
      });

      const response = await fetch(CONTACT_FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });

      const responseText = await response.text();
      let parsed: Record<string, unknown> = {};
      try {
        parsed = JSON.parse(responseText);
      } catch {
        // ignore parse errors
      }

      if (response.ok && (parsed as { code?: string })?.code === 'OK') {
        setFormState('success');
        setFormMessage('您的消息已成功发送，我们会尽快回复！');
        form.reset();
      } else {
        const serverMsg =
          ((parsed as { meta?: { message?: string } })?.meta?.message) ||
          ((parsed as { message?: string })?.message) ||
          '发送失败，请稍后重试或直接发送邮件至 contact@zhangzaichao.cn';
        setFormState('error');
        setFormMessage(serverMsg as string);
      }
    } catch {
      setFormState('error');
      setFormMessage('网络连接异常，请稍后重试。');
    }
  };

  return (
    <>
      <SEOHead
        title="关于我们"
        description="了解水野朝阳官方粉丝站的品牌故事、发展历程与联系方式。我们致力于为全球粉丝提供最权威、最全面的水野朝阳信息聚合服务。"
        keywords="水野朝阳,关于我们,水野朝阳粉丝站,联系我们,品牌介绍"
        canonicalPath="/about"
        ogType="website"
        schema={aboutSchema}
      />
      <ScrollToTop />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-20">
          <div className="px-4 md:px-6 max-w-5xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', path: '/' },
                { label: '关于我们' },
              ]}
            />

            <div className="mb-12">
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
                {aboutData.siteIntro.title}
              </h1>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed max-w-3xl">
                {aboutData.siteIntro.content}
              </p>
              <div className="mt-6 bg-accent-50 border border-accent-200/60 rounded-xl p-5">
                <p className="text-sm text-foreground-700 leading-relaxed">
                  <strong className="text-accent-700">我们的使命：</strong>
                  {aboutData.siteIntro.mission}
                </p>
              </div>
            </div>

            <section className="mb-16">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-6">
                {aboutData.reputation.title}
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                {aboutData.reputation.items.map((item) => (
                  <div
                    key={item.title}
                    className="bg-background-50 border border-background-200/70 rounded-xl p-5 flex gap-4"
                  >
                    <div className="w-10 h-10 shrink-0 flex items-center justify-center rounded-full bg-accent-100">
                      <i className={`${item.icon} text-accent-600 text-lg`} />
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-foreground-800 mb-1.5">{item.title}</h3>
                      <p className="text-xs text-foreground-500 leading-relaxed">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            <section className="mb-16">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-6">
                {aboutData.timeline.title}
              </h2>
              <div className="relative">
                <div className="absolute left-5 top-0 bottom-0 w-px bg-background-300" />
                <div className="space-y-6">
                  {aboutData.timeline.events.map((event, index) => (
                    <div key={index} className="relative flex gap-6 pl-14">
                      <div className="absolute left-0 top-1 w-10 h-10 rounded-full bg-accent-100 border-2 border-accent-300 flex items-center justify-center">
                        <span className="text-xs font-bold text-accent-700">{index + 1}</span>
                      </div>
                      <div className="bg-background-50 border border-background-200/70 rounded-xl p-4 flex-1">
                        <span className="text-xs font-semibold text-accent-600">{event.year}</span>
                        <h3 className="text-sm font-semibold text-foreground-800 mt-1">{event.title}</h3>
                        <p className="text-xs text-foreground-500 mt-1">{event.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            <section className="mb-16">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-3">
                {aboutData.contact.title}
              </h2>
              <p className="text-sm text-foreground-500 mb-8">{aboutData.contact.description}</p>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div className="bg-background-50 border border-background-200/70 rounded-xl p-6">
                  <h3 className="text-sm font-semibold text-foreground-800 mb-4">联系方式</h3>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 flex items-center justify-center rounded-full bg-accent-100">
                        <i className="ri-mail-line text-accent-600" />
                      </div>
                      <div>
                        <p className="text-xs text-foreground-500">电子邮箱</p>
                        <a
                          href={`mailto:${aboutData.contact.email}`}
                          className="text-sm text-foreground-800 hover:text-primary-600 transition-colors"
                        >
                          {aboutData.contact.email}
                        </a>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 flex items-center justify-center rounded-full bg-accent-100">
                        <i className="ri-global-line text-accent-600" />
                      </div>
                      <div className="flex items-center gap-3">
                        {aboutData.contact.socialLinks.map((link) => (
                          <a
                            key={link.platform}
                            href={link.url}
                            target="_blank"
                            rel="nofollow noopener noreferrer"
                            className="flex items-center gap-1.5 text-sm text-foreground-600 hover:text-primary-600 transition-colors"
                          >
                            <i className={link.icon} />
                            {link.platform}
                          </a>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-background-50 border border-background-200/70 rounded-xl p-6">
                  <h3 className="text-sm font-semibold text-foreground-800 mb-4">发送消息</h3>
                  <form onSubmit={handleSubmit} data-readdy-form="">
                    <div className="space-y-3">
                      <div>
                        <input
                          type="text"
                          name="name"
                          placeholder="您的姓名"
                          required
                          className="w-full px-4 py-2.5 text-sm bg-background-100 border border-background-200/70 rounded-lg focus:outline-none focus:border-primary-300 transition-colors"
                        />
                      </div>
                      <div>
                        <input
                          type="email"
                          name="email"
                          placeholder="您的邮箱"
                          required
                          className="w-full px-4 py-2.5 text-sm bg-background-100 border border-background-200/70 rounded-lg focus:outline-none focus:border-primary-300 transition-colors"
                        />
                      </div>
                      <div>
                        <textarea
                          name="message"
                          placeholder="请输入您的留言（500字以内）"
                          required
                          maxLength={500}
                          rows={4}
                          className="w-full px-4 py-2.5 text-sm bg-background-100 border border-background-200/70 rounded-lg focus:outline-none focus:border-primary-300 transition-colors resize-none"
                        />
                      </div>
                      <div className="spam-field">
                        <input
                          type="text"
                          name="phone_alt"
                          tabIndex={-1}
                          autoComplete="off"
                          aria-hidden="true"
                          readOnly
                        />
                      </div>
                      <button
                        type="submit"
                        className="w-full px-6 py-3 bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium rounded-full transition-colors whitespace-nowrap"
                      >
                        发送消息
                      </button>
                    </div>
                    {formState === 'success' && (
                      <p className="mt-3 text-sm text-green-600">{formMessage}</p>
                    )}
                    {formState === 'error' && (
                      <p className="mt-3 text-sm text-red-600">{formMessage}</p>
                    )}
                  </form>
                </div>
              </div>
            </section>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}